/**
 * New Booking Page - Redesigned
 * Step-by-step wizard for creating bookings with modern dark theme support
 */

import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  User,
  Car,
  Calendar,
  FileText,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertCircle,
  Loader2,
  Plus,
  X,
  Shield,
  Wrench,
} from 'lucide-react';
import { Button } from '../components/ui';
import { Modal } from '../components/ui/modal';
import { CustomerSearch } from '../components/forms/CustomerSearch';
import { CustomerForm } from '../components/forms/CustomerForm';
import { VehicleSelect } from '../components/forms/VehicleSelect';
import { VehicleForm } from '../components/forms/VehicleForm';
import { TimeSlotPicker } from '../components/booking/TimeSlotPicker';
import { CaseContextForm, type CaseContextData } from '../components/booking/CaseContextForm';
import { useCreateBooking } from '../hooks/useBookings';
import { useWorkflowByType } from '../hooks/useWorkflowConfig';
import { useToast } from '../contexts/ToastContext';
import { cn } from '@/lib/utils';

// Minimal customer type that works with both CustomerSearch and CustomerForm results
interface SelectedCustomer {
  id: string;
  name: string;
  phone: string;
  email?: string;
}

type Step = 'customer' | 'vehicle' | 'type' | 'datetime' | 'review';

type BookingType = 'private' | 'insurance' | 'warranty';
const CASE_CATEGORY_TO_BOOKING_TYPE: Record<string, BookingType> = {
  particular: 'private',
  seguro: 'insurance',
  garantia: 'warranty',
};

const STEPS: { id: Step; label: string; icon: React.ElementType }[] = [
  { id: 'customer', label: 'Cliente', icon: User },
  { id: 'vehicle', label: 'Vehiculo', icon: Car },
  { id: 'type', label: 'Tipo', icon: FileText },
  { id: 'datetime', label: 'Fecha', icon: Calendar },
  { id: 'review', label: 'Confirmar', icon: Check },
];

interface FormData {
  customerId: string | null;
  vehicleId: string | null;
  caseCategory: 'particular' | 'seguro' | 'garantia';
  scheduledAt: number | null;
  notes: string;
  caseContext: CaseContextData;
}

export function NewBookingPage() {
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const createBookingMutation = useCreateBooking();

  const [currentStep, setCurrentStep] = useState<Step>('customer');
  const [formData, setFormData] = useState<FormData>({
    customerId: null,
    vehicleId: null,
    caseCategory: 'particular',
    scheduledAt: null,
    notes: '',
    caseContext: {},
  });
  const [selectedCustomer, setSelectedCustomer] = useState<SelectedCustomer | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<{ id: string; registration: string; make: string; model: string } | null>(null);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [vehicleRefreshKey, setVehicleRefreshKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const bookingTypeForWorkflow = useMemo(
    () => CASE_CATEGORY_TO_BOOKING_TYPE[formData.caseCategory],
    [formData.caseCategory]
  );
  const { data: bookingWorkflow, isLoading: isWorkflowLoading } =
    useWorkflowByType(bookingTypeForWorkflow);
  const isWorkflowMissing = !isWorkflowLoading && !bookingWorkflow;

  const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep);

  const handleCustomerSelect = (customer: SelectedCustomer) => {
    setSelectedCustomer(customer);
    setFormData((prev) => ({
      ...prev,
      customerId: customer.id,
      vehicleId: null,
    }));
    setSelectedVehicle(null);
  };

  const handleVehicleChange = (vehicleId: string | null, vehicle?: { registration: string; make: string; model: string }) => {
    setFormData((prev) => ({ ...prev, vehicleId }));
    if (vehicle && vehicleId) {
      setSelectedVehicle({ id: vehicleId, ...vehicle });
    } else {
      setSelectedVehicle(null);
    }
  };

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 'customer':
        return !!formData.customerId;
      case 'vehicle':
        return !!formData.vehicleId;
      case 'type':
        if (formData.caseCategory === 'seguro' && !formData.caseContext.insurerCompany?.trim()) {
          return false;
        }
        if (formData.caseCategory === 'garantia' && !formData.caseContext.warrantyType) {
          return false;
        }
        return true;
      case 'datetime':
        return !!formData.scheduledAt;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const goNext = () => {
    const idx = currentStepIndex;
    if (idx < STEPS.length - 1) {
      setCurrentStep(STEPS[idx + 1].id);
      setError(null);
    }
  };

  const goBack = () => {
    const idx = currentStepIndex;
    if (idx > 0) {
      setCurrentStep(STEPS[idx - 1].id);
      setError(null);
    }
  };

  const goToStep = (step: Step) => {
    const targetIdx = STEPS.findIndex((s) => s.id === step);
    if (targetIdx <= currentStepIndex) {
      setCurrentStep(step);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!formData.customerId || !formData.vehicleId || !formData.scheduledAt) {
      setError('Faltan datos requeridos');
      return;
    }
    if (isWorkflowMissing) {
      setError('No hay flujo configurado para este tipo de cita');
      return;
    }

    try {
      const booking = await createBookingMutation.mutateAsync({
        customerId: formData.customerId,
        vehicleId: formData.vehicleId,
        type: formData.caseCategory === 'seguro' ? 'seguro' : 'particular',
        caseCategory: formData.caseCategory,
        scheduledAt: formData.scheduledAt,
        notes: formData.notes || undefined,
        insuranceCompany: formData.caseCategory === 'seguro' ? formData.caseContext.insurerCompany : undefined,
        insuranceClaimNumber: formData.caseCategory === 'seguro' ? formData.caseContext.claimNumber : undefined,
      } as Parameters<typeof createBookingMutation.mutateAsync>[0]);

      showSuccess('Cita creada correctamente');
      navigate(`/bookings/${booking.id}`);
    } catch (err: unknown) {
      const error = err as { message?: string };
      showError(error.message || 'Error al crear la cita');
    }
  };

  const formatDateTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const isSubmitting = createBookingMutation.isPending;

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/bookings"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Volver a citas
        </Link>
        <h1 className="text-2xl font-bold text-foreground">Nueva Cita</h1>
        <p className="text-muted-foreground mt-1">Complete los pasos para agendar una cita</p>
      </div>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {STEPS.map((step, idx) => {
            const isCompleted = idx < currentStepIndex;
            const isCurrent = step.id === currentStep;
            const Icon = step.icon;

            return (
              <div key={step.id} className="flex items-center">
                <button
                  onClick={() => goToStep(step.id)}
                  disabled={idx > currentStepIndex}
                  className={cn(
                    'flex flex-col items-center gap-2 transition-colors',
                    idx <= currentStepIndex ? 'cursor-pointer' : 'cursor-not-allowed'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all',
                      isCompleted
                        ? 'border-primary bg-primary text-primary-foreground'
                        : isCurrent
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-muted bg-muted/50 text-muted-foreground'
                    )}
                  >
                    {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                  </div>
                  <span
                    className={cn(
                      'text-xs font-medium hidden sm:block',
                      isCurrent ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {step.label}
                  </span>
                </button>
                {idx < STEPS.length - 1 && (
                  <div
                    className={cn(
                      'h-0.5 w-8 sm:w-16 mx-2',
                      idx < currentStepIndex ? 'bg-primary' : 'bg-muted'
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 rounded-lg bg-destructive/10 border border-destructive/20 p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      {isWorkflowMissing && (
        <div className="mb-6 rounded-lg bg-amber-50 border border-amber-200 p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700">
            No hay flujo configurado para este tipo de cita. Configúralo antes de crear la reserva.
          </p>
        </div>
      )}

      {/* Step Content */}
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
        {/* Step 1: Customer */}
        {currentStep === 'customer' && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Seleccionar Cliente</h2>
                <p className="text-sm text-muted-foreground">Busque o cree un nuevo cliente</p>
              </div>
            </div>

            <CustomerSearch
              onSelect={handleCustomerSelect}
              onCreateNew={() => setIsCustomerModalOpen(true)}
            />

            {selectedCustomer && (
              <div className="flex items-center justify-between rounded-lg bg-primary/5 border border-primary/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <User className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{selectedCustomer.name}</p>
                    <p className="text-sm text-muted-foreground">{selectedCustomer.phone}</p>
                    {selectedCustomer.email && (
                      <p className="text-xs text-muted-foreground">{selectedCustomer.email}</p>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSelectedCustomer(null);
                    setFormData((prev) => ({ ...prev, customerId: null, vehicleId: null }));
                    setSelectedVehicle(null);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}

            <Button
              variant="secondary"
              onClick={() => setIsCustomerModalOpen(true)}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear nuevo cliente
            </Button>
          </div>
        )}

        {/* Step 2: Vehicle */}
        {currentStep === 'vehicle' && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Car className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Seleccionar Vehiculo</h2>
                <p className="text-sm text-muted-foreground">
                  Vehiculos de {selectedCustomer?.name || 'cliente'}
                </p>
              </div>
            </div>

            <VehicleSelect
              key={vehicleRefreshKey}
              customerId={formData.customerId}
              value={formData.vehicleId}
              onChange={(vehicleId, vehicle) => handleVehicleChange(vehicleId, vehicle)}
              onCreateNew={() => setIsVehicleModalOpen(true)}
            />

            {selectedVehicle && (
              <div className="flex items-center justify-between rounded-lg bg-primary/5 border border-primary/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                    <Car className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{selectedVehicle.registration}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedVehicle.make} {selectedVehicle.model}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Button
              variant="secondary"
              onClick={() => setIsVehicleModalOpen(true)}
              className="w-full"
              disabled={!formData.customerId}
            >
              <Plus className="h-4 w-4 mr-2" />
              Agregar nuevo vehiculo
            </Button>
          </div>
        )}

        {/* Step 3: Booking Type */}
        {currentStep === 'type' && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <FileText className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Tipo de Cita</h2>
                <p className="text-sm text-muted-foreground">Seleccione la categoria</p>
              </div>
            </div>

            {/* Type Selection Pills */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: 'particular', label: 'Particular', icon: User, desc: 'Cliente directo' },
                { value: 'seguro', label: 'Seguro', icon: Shield, desc: 'Compañia de seguros' },
                { value: 'garantia', label: 'Garantia', icon: Wrench, desc: 'Bajo garantia' },
              ].map(({ value, label, icon: Icon, desc }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      caseCategory: value as 'particular' | 'seguro' | 'garantia',
                      caseContext: value === 'particular' ? {} : prev.caseContext,
                    }))
                  }
                  className={cn(
                    'flex flex-col items-center gap-2 rounded-xl border-2 p-4 transition-all',
                    formData.caseCategory === value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border bg-card hover:border-primary/50 text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Icon className="h-6 w-6" />
                  <span className="font-medium text-sm">{label}</span>
                  <span className="text-xs opacity-70">{desc}</span>
                </button>
              ))}
            </div>

            {/* Insurance/Warranty Context Form */}
            {formData.caseCategory !== 'particular' && (
              <div className="mt-6 rounded-lg border border-border bg-muted/30 p-4">
                <CaseContextForm
                  bookingType={formData.caseCategory === 'seguro' ? 'insurance' : 'warranty'}
                  value={formData.caseContext}
                  onChange={(caseContext) => setFormData((prev) => ({ ...prev, caseContext }))}
                  errors={{}}
                />
              </div>
            )}
          </div>
        )}

        {/* Step 4: Date/Time */}
        {currentStep === 'datetime' && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Fecha y Hora</h2>
                <p className="text-sm text-muted-foreground">Seleccione cuando desea la cita</p>
              </div>
            </div>

            <TimeSlotPicker
              value={formData.scheduledAt}
              onChange={(scheduledAt) => setFormData((prev) => ({ ...prev, scheduledAt }))}
              minDate={new Date()}
            />

            {/* Notes */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                Notas adicionales
              </label>
              <textarea
                className="w-full rounded-lg border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder="Detalles adicionales sobre la cita..."
                maxLength={500}
              />
              <p className="mt-1 text-xs text-muted-foreground text-right">
                {formData.notes.length}/500
              </p>
            </div>
          </div>
        )}

        {/* Step 5: Review */}
        {currentStep === 'review' && (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Check className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Confirmar Cita</h2>
                <p className="text-sm text-muted-foreground">Revise los datos antes de crear</p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Customer */}
              <div className="flex items-start gap-4 rounded-lg bg-muted/30 p-4">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Cliente</p>
                  <p className="font-medium text-foreground">{selectedCustomer?.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedCustomer?.phone}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => goToStep('customer')}>
                  Editar
                </Button>
              </div>

              {/* Vehicle */}
              <div className="flex items-start gap-4 rounded-lg bg-muted/30 p-4">
                <Car className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Vehiculo</p>
                  <p className="font-medium text-foreground">{selectedVehicle?.registration}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedVehicle?.make} {selectedVehicle?.model}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => goToStep('vehicle')}>
                  Editar
                </Button>
              </div>

              {/* Type */}
              <div className="flex items-start gap-4 rounded-lg bg-muted/30 p-4">
                <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Tipo</p>
                  <p className="font-medium text-foreground capitalize">{formData.caseCategory}</p>
                  {formData.caseCategory === 'seguro' && formData.caseContext.insurerCompany && (
                    <p className="text-sm text-muted-foreground">{formData.caseContext.insurerCompany}</p>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => goToStep('type')}>
                  Editar
                </Button>
              </div>

              {/* Date/Time */}
              <div className="flex items-start gap-4 rounded-lg bg-muted/30 p-4">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Fecha y Hora</p>
                  <p className="font-medium text-foreground capitalize">
                    {formData.scheduledAt ? formatDateTime(formData.scheduledAt) : '-'}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => goToStep('datetime')}>
                  Editar
                </Button>
              </div>

              {/* Notes */}
              {formData.notes && (
                <div className="flex items-start gap-4 rounded-lg bg-muted/30 p-4">
                  <FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Notas</p>
                    <p className="text-sm text-foreground">{formData.notes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="mt-6 flex items-center justify-between">
        <Button
          variant="secondary"
          onClick={currentStepIndex === 0 ? () => navigate('/bookings') : goBack}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          {currentStepIndex === 0 ? 'Cancelar' : 'Atras'}
        </Button>

        {currentStep === 'review' ? (
          <Button onClick={handleSubmit} disabled={isSubmitting || isWorkflowMissing || isWorkflowLoading}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creando...
              </>
            ) : (
              <>
                <Check className="h-4 w-4 mr-2" />
                Crear Cita
              </>
            )}
          </Button>
        ) : (
          <Button onClick={goNext} disabled={!canProceed()}>
            Siguiente
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Customer Modal */}
      <Modal
        isOpen={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
        title="Nuevo Cliente"
        size="md"
      >
        <CustomerForm
          onSuccess={(customer) => {
            setIsCustomerModalOpen(false);
            handleCustomerSelect(customer);
            setVehicleRefreshKey((k) => k + 1);
          }}
          onCancel={() => setIsCustomerModalOpen(false)}
        />
      </Modal>

      {/* Vehicle Modal */}
      {formData.customerId && (
        <Modal
          isOpen={isVehicleModalOpen}
          onClose={() => setIsVehicleModalOpen(false)}
          title="Agregar Vehiculo"
          size="md"
        >
          <VehicleForm
            customerId={formData.customerId}
            onSuccess={(vehicle) => {
              setIsVehicleModalOpen(false);
              setVehicleRefreshKey((k) => k + 1);
              handleVehicleChange(vehicle.id, {
                registration: vehicle.registration,
                make: vehicle.make,
                model: vehicle.model,
              });
            }}
            onCancel={() => setIsVehicleModalOpen(false)}
          />
        </Modal>
      )}
    </div>
  );
}
